<template>
  <BuildingSectionCard title="Building" :form="BuildingBasicsForm" :form-props="{ building }" has-info>
    <template #default="{ open }">
      <View gap="sm" align="center" wrap="wrap">
        <Text variant="h3" class="name">{{ building.name }}</Text>
        <Badge icon="building-06" label="Building" variant="grey" size="small" />
      </View>

      <PropertyDetailsCardRows>
        <PropertyDetailsCardInfoItem label="Units" icon="door-01" :value="building.unitCount" />
        <PropertyDetailsCardInfoItem
          v-if="building.yearBuilt"
          label="Built"
          icon="calendar"
          :value="building.yearBuilt"
        />
        <!-- The seat is only ever missing for a moment: a removed seat hands its buildings over -->
        <PropertyDetailsCardInfoItem label="Managed by" icon="user-01" :value="building.manager ? undefined : '—'">
          <template v-if="building.manager" #default>
            <Avatar :user-name="building.manager.name" size="small" />
            <Text variant="body-medium">{{ building.manager.name }}</Text>
          </template>
        </PropertyDetailsCardInfoItem>
      </PropertyDetailsCardRows>

      <!-- How a renter gets in for a tour; Tallo says it in tour confirmations -->
      <Hint v-if="building.accessNotes" class="getting-in">
        <template #icon>
          <IconTile icon="key" size="small" color="neutral" />
        </template>
        <View direction="column" gap="xs" class="getting-in-text">
          <Text variant="label-small" color="secondary">Getting in</Text>
          <Text variant="body-medium" class="access-notes" data-testid="access-notes">{{ building.accessNotes }}</Text>
        </View>
      </Hint>
      <PropertyDetailsCardPlaceholder
        v-else
        variant="compact"
        title="No access notes yet"
        subtitle="Renters get these before every showing."
        button-text="Add access notes"
        @button-clicked="open"
      />
    </template>
  </BuildingSectionCard>
</template>

<script setup lang="ts">
import { Avatar, Badge, Hint, IconTile, Text, View } from '@tallo/design-system';
import { BuildingDto } from '@tallo/investor/property';
import { PropertyDetailsCardInfoItem, PropertyDetailsCardPlaceholder, PropertyDetailsCardRows } from '@tallo/property';

import BuildingBasicsForm from '../../../components/building-basics-form.vue';
import BuildingSectionCard from '../../../components/building-section-card.vue';

/** The building's own facts: its name, how many units it holds, when it was built, who runs it, how to get in */
defineProps<{ building: BuildingDto }>();
</script>

<style scoped lang="scss">
// A long name wraps rather than widening the card
.name,
.getting-in-text {
  min-width: 0;
  overflow-wrap: anywhere;
}

// Last in the card, and pinned to the bottom when the location card stretches the row
// Last in the card, and pinned to the bottom when the location card stretches the row
.getting-in {
  margin-top: auto;
}

// Notes are typed with line breaks (buzzer, then parking); they read the way they were written
.access-notes {
  white-space: pre-line;
}
</style>
